import type { Directives } from 'mdast-util-directive';
import { useCallback, useEffect, useRef } from 'react';
import { faSquarePollVertical } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { mergeRegister } from '@lexical/utils';
import { $isDirectiveNode, DirectiveEditorProps } from '@mdxeditor/editor';
import { Stack } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import {
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
} from 'lexical';

import type { IPollMdxDirectiveAttributes } from './PollMdxDirectiveToolbarButton';
import { QuerySkeleton } from '~/components/atoms/QuerySkeleton';
import { usePollQuery } from '~/lib/api/modules/Polls/hooks/usePollQuery';

const styles = stylex.create({
  frame: {
    pointerEvents: 'none',
  },
  frame$selected: {
    outlineWidth: '2px',
    outlineStyle: 'solid',
    outlineColor: 'blue',
  },
});

// Inspired from:
// https://github.com/mdx-editor/editor/blob/main/src/plugins/image/ImageEditor.tsx

export const PollMdxDirectiveEditor: React.ComponentType<
  DirectiveEditorProps<Directives>
> = (props) => {
  const { lexicalNode, mdastNode } = props;
  const attributes = mdastNode.attributes as IPollMdxDirectiveAttributes;
  const nodeKey = lexicalNode.getKey();
  const [editor] = useLexicalComposerContext();
  const [selected, setSelected, clearSelection] =
    useLexicalNodeSelection(nodeKey);
  const editorRef = useRef<HTMLParagraphElement>(null);
  const pollQuery = usePollQuery({
    appId: attributes.appId,
    pollId: attributes.pollId,
  });

  const handleDelete = useCallback(
    (payload: KeyboardEvent): boolean => {
      if (selected && $isNodeSelection($getSelection())) {
        const event: KeyboardEvent = payload;
        event.preventDefault();
        const node = $getNodeByKey(nodeKey);
        if ($isDirectiveNode(node)) {
          node.remove();
        }
        return true;
      }

      return false;
    },
    [nodeKey, selected],
  );

  useEffect(() => {
    const unregister = mergeRegister(
      editor.registerCommand<MouseEvent>(
        CLICK_COMMAND,
        (event) => {
          if (event.target === editorRef.current) {
            if (event.shiftKey) {
              setSelected(!selected);
            } else {
              clearSelection();
              setSelected(true);
            }

            return true;
          }

          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        KEY_DELETE_COMMAND,
        handleDelete,
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        KEY_BACKSPACE_COMMAND,
        handleDelete,
        COMMAND_PRIORITY_LOW,
      ),
    );

    return () => {
      unregister();
    };
  }, [editor, handleDelete, selected, setSelected, clearSelection]);

  return (
    <p ref={editorRef}>
      <Stack
        as="span"
        horizontal
        sx={[styles.frame, selected && styles.frame$selected]}
        gap={1}
      >
        <FontAwesomeIcon
          icon={faSquarePollVertical}
          fontSize={18}
          style={{ padding: 3 }}
        />
        <QuerySkeleton query={pollQuery}>{pollQuery.data?.title}</QuerySkeleton>
      </Stack>
    </p>
  );
};
